<script lang="ts" setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import Swiper from 'swiper'
import { Autoplay, Pagination } from 'swiper/modules'
import { useElementClick } from '@/composables/tracks'
import 'swiper/css'
import 'swiper/css/pagination'

const props = defineProps<{
  module: any
  gotoBook: (id: string, firstChapterId?: string) => void
}>()

const { elementClick } = useElementClick()
// Use the actual book cover, rather than the campaign background image.
const books = computed<any[]>(() => (props.module?.resourceList?.records || []).filter((book: any) => book.id && book.contentCoverUrl))
const container = ref<HTMLElement | null>(null)
const pagination = ref<HTMLElement | null>(null)
let swiper: Swiper | null = null

function openBook(book: any) {
  elementClick(`banner_${props.module.id}_${book.id}`)
  props.gotoBook(book.id, book.firstChapter?.id)
}

function initSwiper() {
  swiper?.destroy(true, true)
  swiper = null
  if (!container.value || books.value.length < 2)
    return
  swiper = new Swiper(container.value, {
    modules: [Autoplay, Pagination],
    slidesPerView: 1,
    loop: true,
    autoplay: { delay: 4000, disableOnInteraction: false },
    pagination: pagination.value ? { el: pagination.value, clickable: true } : undefined,
  })
}

watch(books, async () => {
  await nextTick()
  initSwiper()
})
onMounted(initSwiper)
onBeforeUnmount(() => swiper?.destroy(true, true))
</script>

<template>
  <section v-if="books.length" class="sprig-banner">
    <div ref="container" class="swiper">
      <div class="swiper-wrapper">
        <div v-for="book in books" :key="book.id" class="swiper-slide sprig-slide">
          <div class="sprig-copy">
            <p class="sprig-eyebrow">
              THE CAMPUS EDIT
            </p>
            <h2 class="sprig-title" :title="book.contentName">
              {{ book.contentName }}
            </h2>
            <p class="sprig-description">
              {{ book.introduce }}
            </p>
            <button type="button" class="sprig-explore" @click="openBook(book)">
              Explore stories ↗
            </button>
          </div>
          <button type="button" class="sprig-cover-button" :aria-label="book.contentName" @click="openBook(book)">
            <img :src="book.contentCoverUrl" :alt="book.contentName || ''" class="sprig-book-cover">
          </button>
          <span class="sprig-sparkle" aria-hidden="true">✧</span>
        </div>
      </div>
    </div>
    <div v-show="books.length > 1" ref="pagination" class="banner-pagination" />
  </section>
</template>

<style lang="scss" scoped>
.sprig-banner {
  position: relative;
  overflow: hidden;
  border-radius: 20px;
  background: #D1FAE5;
  color: #1F2937;
}
.sprig-banner .sprig-slide {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  height: auto;
  text-align: left;
  position: relative;
  box-sizing: border-box;
  padding: 24px 20px 36px;
  min-height: 260px;
}
.sprig-copy { position: relative; z-index: 2; width: 61%; margin: 0; }
.sprig-eyebrow { font-size: 9px; font-weight: 700; letter-spacing: 2px; }
.sprig-banner .sprig-title {
  margin-top: 14px;
  font-family: Georgia, 'Times New Roman', serif;
  font-size: 31px;
  line-height: 1.12;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  overflow: hidden;
  overflow-wrap: anywhere;
  font-weight: 500;
  letter-spacing: -0.7px;
}
.sprig-banner .sprig-description {
  margin-top: 14px;
  font-size: 11px;
  line-height: 1.5;
  color: #4B5563;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  overflow-wrap: anywhere;
}
.sprig-explore {
  margin-top: 12px;
  padding: 8px 15px;
  border: 0;
  border-radius: 999px;
  background: linear-gradient(90deg, #10B981 0%, #059669 100%);
  color: #FFFFFF;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
}
.sprig-cover-button {
  position: absolute;
  z-index: 3;
  right: 19px;
  top: 50%;
  width: 26%;
  padding: 0;
  border: 0;
  border-radius: 9px;
  background: transparent;
  transform: translateY(-50%) rotate(10deg);
  cursor: pointer;
  box-shadow: 0 15px 22px #1F293726;
}
.sprig-book-cover { display: block; width: 100%; aspect-ratio: 3 / 4; object-fit: cover; border-radius: inherit; }
.sprig-sparkle { position: absolute; right: -8px; top: 35px; font-size: 145px; line-height: 1; color: #D3E5DA; pointer-events: none; }
.banner-pagination {
  position: absolute;
  bottom: 12px;
  left: 0;
  width: 100%;
  z-index: 4;
  text-align: center;
  --swiper-pagination-color: #10B981;
  --swiper-pagination-bullet-inactive-color: #94B6A2;
  :deep(.swiper-pagination-bullet) {
    width: 5px;
    height: 5px;
    margin: 0 5px;
    border-radius: 50%;
    opacity: 0.55;
    transition: background-color 0.2s ease, box-shadow 0.2s ease;
  }
  :deep(.swiper-pagination-bullet-active) {
    opacity: 1;
    box-shadow: 0 0 0 2px #D1FAE5, 0 0 0 3px #10B981;
  }
}
</style>
